/**
 * The Syria3Hours mark: the map of Syria holding a clock whose pink quarter is a 3-hour window on
 * a 12-hour dial, with two broadcast waves. Same geometry as the image cards and docs/brand.
 */
const MAP =
  "M42.5,155.8 L44.5,155.2 Q53,153 61.5,143.6 L63.5,141.4 Q72,132 78.3,128 L79.7,127 Q86,123 84.7,110.4 L84.3,107.6 Q83,95 88.4,82.4 L89.6,79.6 Q95,67 105.8,72.8 L108.2,74.2 Q119,80 128,82.2 L130,82.8 Q139,85 153.8,85.5 L157.2,85.5 Q172,86 186.8,77.5 L190.2,75.5 Q205,67 223.4,64.3 L227.6,63.7 Q246,61 262.6,70.5 L266.4,72.5 Q283,82 304.6,78 L352.4,69 Q374,65 395.4,59.9 L445.6,48.1 Q467,43 488.2,37.1 L510.8,30.9 Q532,25 539.6,21.9 L541.4,21.1 Q549,18 554.9,27.5 L556.1,29.5 Q562,39 551.6,52 L549.4,55 Q539,68 521,80.6 L490,102.4 Q472,115 474,136.9 L477,168.1 Q479,190 475.9,211.8 L470.1,253.2 Q467,275 459.4,289.9 L457.6,293.1 Q450,308 431,319 L288,402 Q269,413 250.7,425.2 L128.3,506.8 Q110,519 93.8,515.9 L90.2,515.1 Q74,512 61.8,501.2 L59.2,498.8 Q47,488 34.9,483.5 L32.1,482.5 Q20,478 16.4,467.6 L15.6,465.4 Q12,455 18.3,440.6 L19.7,437.4 Q26,423 27.4,416.2 L27.6,414.8 Q29,408 36.2,400.8 L37.8,399.2 Q45,392 57.1,378.5 L59.9,375.5 Q72,362 81,346.7 L83,343.3 Q92,328 85.7,310.9 L84.3,307.1 Q78,290 72.2,287.8 L70.8,287.2 Q65,285 52.9,285.4 L50.1,285.6 Q38,286 34.9,274.3 L34.1,271.7 Q31,260 33.2,247.4 L33.8,244.6 Q36,232 30.1,216.7 L28.9,213.3 Q23,198 24.8,183.2 L25.2,179.8 Q27,165 30.1,161.8 L30.9,161.2 Q34,158 42.5,155.8 Z";
const VIEW = { x: 14.3, y: 20.4, width: 544, height: 496.1 };
const CX = 250;
const CY = 255;

function at(r: number, deg: number) {
  const a = (deg * Math.PI) / 180;
  return `${(CX + r * Math.cos(a)).toFixed(1)},${(CY + r * Math.sin(a)).toFixed(1)}`;
}

function radial(r0: number, r1: number, deg: number) {
  return `M${at(r0, deg)} L${at(r1, deg)}`;
}

export function BrandMark({
  quadrant,
  size = 40,
  land = "var(--c-text)",
  cut = "var(--c-bg)",
  label,
}: {
  /** 0 fills 12–3, 1 fills 3–6, 2 fills 6–9, 3 fills 9–12; null fills the whole face. */
  quadrant: number | null;
  /** Height in pixels. */
  size?: number;
  /** The map. */
  land?: string;
  /** The bezel, ticks and hands: usually the colour behind the mark. */
  cut?: string;
  label?: string;
}) {
  const start = quadrant === null ? -90 : quadrant * 90 - 90;
  const end = quadrant === null ? -90 : start + 90;
  return (
    <svg
      height={size}
      width={(size * VIEW.width) / VIEW.height}
      viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.width} ${VIEW.height}`}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className="shrink-0"
    >
      <path d={MAP} fill={land} />
      <circle cx={CX} cy={CY} r={118} fill="none" stroke={cut} strokeWidth={9} />
      {quadrant === null ? (
        <circle cx={CX} cy={CY} r={104} fill="var(--c-accent)" />
      ) : (
        <path
          d={`M${CX},${CY} L${at(104, start)} A104,104 0 0 1 ${at(104, end)} Z`}
          fill="var(--c-accent)"
        />
      )}
      <g fill="none" stroke="var(--c-accent)" strokeWidth={13} strokeLinecap="round">
        {[140, 168].map((r) => (
          <path key={r} d={`M${at(r, -68)} A${r},${r} 0 0 1 ${at(r, -22)}`} />
        ))}
      </g>
      <g stroke={cut} strokeLinecap="round">
        {Array.from({ length: 12 }, (_, hour) => (
          <path
            key={hour}
            d={radial(hour % 3 ? 96 : 88, 104, hour * 30 - 90)}
            strokeWidth={hour % 3 ? 5 : 8}
          />
        ))}
        {/* Long hand on the window's start, short hand on its end. */}
        <path d={radial(0, 76, start)} strokeWidth={12} />
        <path d={radial(0, 60, end)} strokeWidth={12} />
      </g>
      <circle cx={CX} cy={CY} r={12} fill={cut} />
      <circle cx={CX} cy={CY} r={4.5} fill={land} />
    </svg>
  );
}
